import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { milestoneApi, taskApi, projectApi, userApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import {
  Plus, PencilSimple, Trash, Flag, ListChecks,
  CaretDown, CaretRight, CheckCircle, Circle,
  CalendarBlank, User, Warning
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const STATUS_OPTIONS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD', 'CANCELLED'];
const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const getStatusVariant = (s) => {
  switch (s) {
    case 'COMPLETED': return 'success';
    case 'IN_PROGRESS': return 'warning';
    case 'ON_HOLD': case 'CANCELLED': return 'danger';
    default: return 'default';
  }
};

const getPriorityVariant = (p) => {
  switch (p) {
    case 'CRITICAL': return 'danger';
    case 'HIGH': return 'warning';
    case 'MEDIUM': return 'primary';
    default: return 'default';
  }
};

export default function MilestonesTasksPage() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [tasksByMilestone, setTasksByMilestone] = useState({});
  const [expandedMilestones, setExpandedMilestones] = useState(new Set());
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Milestone form
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    name: '', planned_date: '', description: '', status: 'PENDING'
  });

  // Task form
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskMilestoneId, setTaskMilestoneId] = useState('');
  const [taskForm, setTaskForm] = useState({
    name: '', assigned_to: '', start_date: '', end_date: '', status: 'PENDING', priority: 'MEDIUM'
  });

  // Subtask form
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [subtaskParentId, setSubtaskParentId] = useState('');
  const [subtaskForm, setSubtaskForm] = useState({
    name: '', assigned_to: '', status: 'PENDING'
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Data loading ----
  useEffect(() => {
    const init = async () => {
      try {
        const [pRes, uRes] = await Promise.all([projectApi.list(), userApi.list()]);
        setProjects(pRes.data);
        setUsers(uRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  const loadMilestones = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const res = await milestoneApi.list(selectedProject);
      setMilestones(res.data);
      setTasksByMilestone({});
      setExpandedMilestones(new Set());
      setExpandedTasks(new Set());
    } catch (err) { console.error(err); }
  }, [selectedProject]);

  useEffect(() => { loadMilestones(); }, [loadMilestones]);

  const loadTasks = useCallback(async (milestoneId) => {
    try {
      const res = await taskApi.list(milestoneId);
      setTasksByMilestone(prev => ({ ...prev, [milestoneId]: res.data }));
    } catch (err) { console.error(err); }
  }, []);

  // ---- Toggle helpers ----
  const toggleMilestone = (id) => {
    setExpandedMilestones(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
    if (!tasksByMilestone[id]) loadTasks(id);
  };

  const toggleTask = (id) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const expandAll = () => {
    const mIds = new Set(milestones.map(m => m.id));
    setExpandedMilestones(mIds);
    milestones.forEach(m => { if (!tasksByMilestone[m.id]) loadTasks(m.id); });
  };
  const collapseAll = () => { setExpandedMilestones(new Set()); setExpandedTasks(new Set()); };

  // ---- Helpers ----
  const getUserName = (id) => users.find(u => u.id === id)?.name || '-';

  const getMilestoneProgress = (milestoneId) => {
    const mTasks = tasksByMilestone[milestoneId] || [];
    if (mTasks.length === 0) return { total: 0, completed: 0, pct: 0 };
    const completed = mTasks.filter(t => t.status === 'COMPLETED').length;
    return { total: mTasks.length, completed, pct: Math.round((completed / mTasks.length) * 100) };
  };

  // Summary across all milestones
  const summary = useMemo(() => {
    let totalTasks = 0, completedTasks = 0, inProgress = 0;
    Object.values(tasksByMilestone).forEach(tList => {
      tList.forEach(t => {
        totalTasks++;
        if (t.status === 'COMPLETED') completedTasks++;
        if (t.status === 'IN_PROGRESS') inProgress++;
      });
    });
    return { milestones: milestones.length, totalTasks, completedTasks, inProgress };
  }, [milestones, tasksByMilestone]);

  // ---- Milestone CRUD ----
  const openCreateMilestone = () => {
    setEditingMilestone(null);
    setMilestoneForm({ name: '', planned_date: '', description: '', status: 'PENDING' });
    setError('');
    setShowMilestoneModal(true);
  };

  const openEditMilestone = (m) => {
    setEditingMilestone(m);
    setMilestoneForm({
      name: m.name,
      planned_date: m.planned_date ? m.planned_date.split('T')[0] : '',
      description: m.description || '',
      status: m.status
    });
    setError('');
    setShowMilestoneModal(true);
  };

  const handleMilestoneSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const data = {
        ...milestoneForm,
        project_id: selectedProject,
        planned_date: milestoneForm.planned_date ? new Date(milestoneForm.planned_date).toISOString() : null
      };
      if (editingMilestone) {
        await milestoneApi.update(editingMilestone.id, data);
      } else {
        await milestoneApi.create(data);
      }
      setShowMilestoneModal(false);
      loadMilestones();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save milestone');
    } finally { setSaving(false); }
  };

  const handleDeleteMilestone = async (id) => {
    if (!window.confirm('Delete this milestone and all its tasks/subtasks?')) return;
    try { await milestoneApi.delete(id); loadMilestones(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  // ---- Task CRUD ----
  const openCreateTask = (milestoneId) => {
    setEditingTask(null);
    setTaskMilestoneId(milestoneId);
    setTaskForm({ name: '', assigned_to: '', start_date: '', end_date: '', status: 'PENDING', priority: 'MEDIUM' });
    setError('');
    setShowTaskModal(true);
  };

  const openEditTask = (task, milestoneId) => {
    setEditingTask(task);
    setTaskMilestoneId(milestoneId);
    setTaskForm({
      name: task.name,
      assigned_to: task.assigned_to || '',
      start_date: task.start_date ? task.start_date.split('T')[0] : '',
      end_date: task.end_date ? task.end_date.split('T')[0] : '',
      status: task.status,
      priority: task.priority
    });
    setError('');
    setShowTaskModal(true);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const data = {
        ...taskForm,
        milestone_id: taskMilestoneId,
        start_date: taskForm.start_date ? new Date(taskForm.start_date).toISOString() : null,
        end_date: taskForm.end_date ? new Date(taskForm.end_date).toISOString() : null,
        assigned_to: taskForm.assigned_to || null
      };
      if (editingTask) {
        await taskApi.update(editingTask.id, data);
      } else {
        await taskApi.create(data);
      }
      setShowTaskModal(false);
      loadTasks(taskMilestoneId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save task');
    } finally { setSaving(false); }
  };

  const handleDeleteTask = async (taskId, milestoneId) => {
    if (!window.confirm('Delete this task and its subtasks?')) return;
    try { await taskApi.delete(taskId); loadTasks(milestoneId); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  // Quick status cycle for tasks
  const cycleTaskStatus = async (task, milestoneId) => {
    const order = ['PENDING', 'IN_PROGRESS', 'COMPLETED'];
    const idx = order.indexOf(task.status);
    const next = order[(idx + 1) % order.length];
    try {
      await taskApi.update(task.id, { status: next });
      loadTasks(milestoneId);
    } catch (err) { console.error(err); }
  };

  // ---- Subtask CRUD ----
  const openCreateSubtask = (taskId) => {
    setEditingSubtask(null);
    setSubtaskParentId(taskId);
    setSubtaskForm({ name: '', assigned_to: '', status: 'PENDING' });
    setError('');
    setShowSubtaskModal(true);
  };

  const openEditSubtask = (subtask, taskId) => {
    setEditingSubtask(subtask);
    setSubtaskParentId(taskId);
    setSubtaskForm({
      name: subtask.name,
      assigned_to: subtask.assigned_to || '',
      status: subtask.status
    });
    setError('');
    setShowSubtaskModal(true);
  };

  const handleSubtaskSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const data = {
        task_id: subtaskParentId,
        name: subtaskForm.name,
        assigned_to: subtaskForm.assigned_to || null,
        status: subtaskForm.status
      };
      if (editingSubtask) {
        await taskApi.updateSubtask(subtaskParentId, editingSubtask.id, data);
      } else {
        await taskApi.createSubtask(subtaskParentId, data);
      }
      setShowSubtaskModal(false);
      // Refresh the parent milestone tasks
      const milestoneId = Object.keys(tasksByMilestone).find(mId =>
        (tasksByMilestone[mId] || []).some(t => t.id === subtaskParentId)
      );
      if (milestoneId) loadTasks(milestoneId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save subtask');
    } finally { setSaving(false); }
  };

  const handleDeleteSubtask = async (taskId, subtaskId) => {
    try {
      await taskApi.deleteSubtask(taskId, subtaskId);
      const milestoneId = Object.keys(tasksByMilestone).find(mId =>
        (tasksByMilestone[mId] || []).some(t => t.id === taskId)
      );
      if (milestoneId) loadTasks(milestoneId);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete subtask'); }
  };

  // Toggle subtask between PENDING and COMPLETED inline
  const toggleSubtaskStatus = async (subtask, taskId) => {
    const next = subtask.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
    try {
      await taskApi.updateSubtask(taskId, subtask.id, { status: next });
      const milestoneId = Object.keys(tasksByMilestone).find(mId =>
        (tasksByMilestone[mId] || []).some(t => t.id === taskId)
      );
      if (milestoneId) loadTasks(milestoneId);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" data-testid="milestones-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Milestones & Tasks</h1>
          <p className="text-[#666666]">Plan and track project milestones, tasks and subtasks</p>
        </div>
      </div>

      {/* Project selector + actions */}
      <div className="flex items-center justify-between">
        <Select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="w-64"
          data-testid="select-project-milestones"
        >
          <option value="">-- Select Project --</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        {selectedProject && (
          <div className="flex items-center gap-2">
            {milestones.length > 0 && (
              <>
                <button onClick={expandAll} className="text-xs text-[#002FA7] hover:underline px-2 py-1" data-testid="expand-all-btn">Expand All</button>
                <span className="text-[#E0E0E0]">|</span>
                <button onClick={collapseAll} className="text-xs text-[#666666] hover:underline px-2 py-1" data-testid="collapse-all-btn">Collapse All</button>
              </>
            )}
            <Button onClick={openCreateMilestone} data-testid="add-milestone-btn">
              <Plus className="w-4 h-4 mr-2" /> New Milestone
            </Button>
          </div>
        )}
      </div>

      {/* Summary cards (when project selected and milestones loaded) */}
      {selectedProject && milestones.length > 0 && (
        <div className="grid grid-cols-4 gap-4" data-testid="milestone-summary">
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3">
            <p className="text-xs text-[#666666] uppercase tracking-wide">Milestones</p>
            <p className="text-xl font-bold text-[#0A0A0A]" data-testid="summary-milestone-count">{summary.milestones}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3">
            <p className="text-xs text-[#666666] uppercase tracking-wide">Total Tasks</p>
            <p className="text-xl font-bold text-[#0A0A0A]" data-testid="summary-task-count">{summary.totalTasks}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3">
            <p className="text-xs text-[#666666] uppercase tracking-wide">Completed</p>
            <p className="text-xl font-bold text-[#00C853]" data-testid="summary-completed-count">{summary.completedTasks}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3">
            <p className="text-xs text-[#666666] uppercase tracking-wide">In Progress</p>
            <p className="text-xl font-bold text-[#FFAB00]" data-testid="summary-inprogress-count">{summary.inProgress}</p>
          </div>
        </div>
      )}

      {/* Empty states */}
      {!selectedProject && (
        <Card className="p-8">
          <div className="text-center text-[#666666]">
            <Flag className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
            <p>Select a project to view milestones and tasks</p>
          </div>
        </Card>
      )}

      {selectedProject && milestones.length === 0 && (
        <Card className="p-8">
          <div className="text-center text-[#666666]">
            <Flag className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
            <p>No milestones yet.</p>
            <p className="text-sm mt-1">Click "New Milestone" to create your first.</p>
          </div>
        </Card>
      )}

      {/* ===== Milestones List ===== */}
      {selectedProject && milestones.length > 0 && (
        <div className="space-y-3">
          {milestones.map(milestone => {
            const isExpanded = expandedMilestones.has(milestone.id);
            const progress = getMilestoneProgress(milestone.id);
            const mTasks = tasksByMilestone[milestone.id] || [];

            return (
              <Card key={milestone.id} className="overflow-hidden">
                {/* Milestone Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F9F9F9] transition-colors"
                  onClick={() => toggleMilestone(milestone.id)}
                  data-testid={`milestone-row-${milestone.id}`}
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    {isExpanded
                      ? <CaretDown className="w-5 h-5 text-[#002FA7] shrink-0" weight="bold" />
                      : <CaretRight className="w-5 h-5 text-[#666666] shrink-0" weight="bold" />
                    }
                    <Flag className="w-5 h-5 text-[#002FA7] shrink-0" weight="fill" />
                    <div className="min-w-0">
                      <p className="font-semibold text-[#0A0A0A] truncate">{milestone.name}</p>
                      <div className="flex items-center gap-3 text-xs text-[#666666]">
                        {milestone.planned_date && (
                          <span className="flex items-center gap-1">
                            <CalendarBlank className="w-3 h-3" />
                            {format(new Date(milestone.planned_date), 'MMM dd, yyyy')}
                          </span>
                        )}
                        {milestone.description && (
                          <span className="truncate max-w-[200px]">{milestone.description}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 shrink-0">
                    {/* Progress bar (once tasks loaded) */}
                    {tasksByMilestone[milestone.id] && progress.total > 0 && (
                      <div className="flex items-center gap-2 w-32" data-testid={`milestone-progress-${milestone.id}`}>
                        <div className="flex-1 h-1.5 bg-[#E0E0E0] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${progress.pct}%`,
                              backgroundColor: progress.pct === 100 ? '#00C853' : '#002FA7'
                            }}
                          />
                        </div>
                        <span className="text-[10px] font-mono text-[#666666] w-8 text-right">{progress.pct}%</span>
                      </div>
                    )}
                    <Badge variant={getStatusVariant(milestone.status)} data-testid={`milestone-status-${milestone.id}`}>
                      {milestone.status}
                    </Badge>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditMilestone(milestone); }}
                      className="p-1.5 rounded text-[#666666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
                      data-testid={`edit-milestone-btn-${milestone.id}`}
                    >
                      <PencilSimple className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteMilestone(milestone.id); }}
                      className="p-1.5 rounded text-[#666666] hover:text-[#FF3333] hover:bg-[#FF3333]/5 transition-colors"
                      data-testid={`delete-milestone-btn-${milestone.id}`}
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* ===== Tasks Panel ===== */}
                {isExpanded && (
                  <div className="border-t border-[#E0E0E0] bg-[#FAFAFA]">
                    <div className="px-4 py-2.5 flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#666666] uppercase tracking-wide">
                        Tasks ({mTasks.length})
                      </span>
                      <Button size="sm" onClick={() => openCreateTask(milestone.id)} data-testid={`add-task-btn-${milestone.id}`}>
                        <Plus className="w-3 h-3 mr-1" /> Add Task
                      </Button>
                    </div>

                    {mTasks.length === 0 ? (
                      <p className="text-sm text-[#666666] text-center py-6 pb-8">
                        No tasks yet. Add the first task for this milestone.
                      </p>
                    ) : (
                      <div className="px-4 pb-4 space-y-2">
                        {mTasks.map(task => {
                          const taskExpanded = expandedTasks.has(task.id);
                          const subtaskCount = task.subtasks?.length || 0;
                          const subtasksDone = task.subtasks?.filter(s => s.status === 'COMPLETED').length || 0;

                          return (
                            <div key={task.id} className="bg-white rounded-lg border border-[#E0E0E0] overflow-hidden" data-testid={`task-card-${task.id}`}>
                              {/* Task row */}
                              <div className="flex items-center justify-between p-3">
                                <div className="flex items-center space-x-3 min-w-0">
                                  {/* Status click-cycle */}
                                  <button
                                    onClick={() => cycleTaskStatus(task, milestone.id)}
                                    className="shrink-0"
                                    title={`Status: ${task.status} (click to cycle)`}
                                    data-testid={`task-status-toggle-${task.id}`}
                                  >
                                    {task.status === 'COMPLETED'
                                      ? <CheckCircle className="w-5 h-5 text-[#00C853]" weight="fill" />
                                      : task.status === 'IN_PROGRESS'
                                        ? <Circle className="w-5 h-5 text-[#FFAB00]" weight="fill" />
                                        : <Circle className="w-5 h-5 text-[#E0E0E0]" />
                                    }
                                  </button>
                                  <div className="min-w-0">
                                    <p className={`text-sm font-medium ${task.status === 'COMPLETED' ? 'text-[#999] line-through' : 'text-[#0A0A0A]'}`}>
                                      {task.name}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-[#666666]">
                                      {task.assigned_to && (
                                        <span className="flex items-center gap-0.5">
                                          <User className="w-3 h-3" />
                                          {getUserName(task.assigned_to)}
                                        </span>
                                      )}
                                      {task.start_date && (
                                        <span className="flex items-center gap-0.5">
                                          <CalendarBlank className="w-3 h-3" />
                                          {format(new Date(task.start_date), 'MMM dd')}
                                          {task.end_date && ` - ${format(new Date(task.end_date), 'MMM dd')}`}
                                        </span>
                                      )}
                                      {subtaskCount > 0 && (
                                        <span>{subtasksDone}/{subtaskCount} subtasks</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-1.5 shrink-0">
                                  <Badge variant={getPriorityVariant(task.priority)}>{task.priority}</Badge>
                                  <button
                                    onClick={() => toggleTask(task.id)}
                                    className="p-1 text-[#666666] hover:text-[#002FA7] rounded transition-colors"
                                    title="Toggle subtasks"
                                    data-testid={`toggle-subtasks-${task.id}`}
                                  >
                                    {taskExpanded ? <CaretDown className="w-3.5 h-3.5" /> : <CaretRight className="w-3.5 h-3.5" />}
                                  </button>
                                  <button
                                    onClick={() => openEditTask(task, milestone.id)}
                                    className="p-1 text-[#666666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 rounded transition-colors"
                                    data-testid={`edit-task-btn-${task.id}`}
                                  >
                                    <PencilSimple className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTask(task.id, milestone.id)}
                                    className="p-1 text-[#666666] hover:text-[#FF3333] hover:bg-[#FF3333]/5 rounded transition-colors"
                                    data-testid={`delete-task-btn-${task.id}`}
                                  >
                                    <Trash className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>

                              {/* Subtask panel */}
                              {taskExpanded && (
                                <div className="border-t border-[#E0E0E0] bg-[#F9F9F9] px-3 py-2.5">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-semibold text-[#999] uppercase tracking-wider">Subtasks</span>
                                    <button
                                      onClick={() => openCreateSubtask(task.id)}
                                      className="text-xs text-[#002FA7] hover:underline flex items-center gap-0.5"
                                      data-testid={`add-subtask-btn-${task.id}`}
                                    >
                                      <Plus className="w-3 h-3" /> Add
                                    </button>
                                  </div>

                                  {subtaskCount === 0 ? (
                                    <p className="text-xs text-[#999] text-center py-2">No subtasks</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {task.subtasks.map(st => (
                                        <div
                                          key={st.id}
                                          className="flex items-center justify-between p-2 bg-white rounded border border-[#EAEAEA]"
                                          data-testid={`subtask-row-${st.id}`}
                                        >
                                          <div className="flex items-center space-x-2 min-w-0">
                                            <button
                                              onClick={() => toggleSubtaskStatus(st, task.id)}
                                              title={st.status === 'COMPLETED' ? 'Mark pending' : 'Mark complete'}
                                              data-testid={`subtask-toggle-${st.id}`}
                                            >
                                              <CheckCircle
                                                className={`w-4 h-4 ${st.status === 'COMPLETED' ? 'text-[#00C853]' : 'text-[#D0D0D0]'}`}
                                                weight={st.status === 'COMPLETED' ? 'fill' : 'regular'}
                                              />
                                            </button>
                                            <span className={`text-xs ${st.status === 'COMPLETED' ? 'text-[#999] line-through' : 'text-[#0A0A0A]'}`}>
                                              {st.name}
                                            </span>
                                            {st.assigned_to && (
                                              <span className="text-[10px] text-[#999]">{getUserName(st.assigned_to)}</span>
                                            )}
                                          </div>
                                          <div className="flex items-center space-x-1 shrink-0">
                                            <Badge variant={getStatusVariant(st.status)}>{st.status}</Badge>
                                            <button
                                              onClick={() => openEditSubtask(st, task.id)}
                                              className="p-0.5 text-[#999] hover:text-[#002FA7] rounded transition-colors"
                                              data-testid={`edit-subtask-btn-${st.id}`}
                                            >
                                              <PencilSimple className="w-3 h-3" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteSubtask(task.id, st.id)}
                                              className="p-0.5 text-[#999] hover:text-[#FF3333] rounded transition-colors"
                                              data-testid={`delete-subtask-btn-${st.id}`}
                                            >
                                              <Trash className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ===== Milestone Modal ===== */}
      <Modal isOpen={showMilestoneModal} onClose={() => setShowMilestoneModal(false)} title={editingMilestone ? 'Edit Milestone' : 'New Milestone'}>
        <form onSubmit={handleMilestoneSubmit} className="space-y-4" data-testid="milestone-form">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm" data-testid="milestone-form-error">{error}</div>}
          <Input label="Name" value={milestoneForm.name} onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })} required data-testid="milestone-name-input" />
          <Input label="Planned Date" type="date" value={milestoneForm.planned_date} onChange={(e) => setMilestoneForm({ ...milestoneForm, planned_date: e.target.value })} required data-testid="milestone-date-input" />
          <Input label="Description" value={milestoneForm.description} onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })} data-testid="milestone-description-input" />
          <Select label="Status" value={milestoneForm.status} onChange={(e) => setMilestoneForm({ ...milestoneForm, status: e.target.value })} data-testid="milestone-status-select">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowMilestoneModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-milestone-btn">{saving ? 'Saving...' : editingMilestone ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* ===== Task Modal ===== */}
      <Modal isOpen={showTaskModal} onClose={() => setShowTaskModal(false)} title={editingTask ? 'Edit Task' : 'New Task'}>
        <form onSubmit={handleTaskSubmit} className="space-y-4" data-testid="task-form">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm" data-testid="task-form-error">{error}</div>}
          <Input label="Task Name" value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })} required data-testid="task-name-input" />
          <Select label="Assigned To" value={taskForm.assigned_to} onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })} data-testid="task-assigned-select">
            <option value="">-- Unassigned --</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start Date" type="date" value={taskForm.start_date} onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })} data-testid="task-start-date-input" />
            <Input label="End Date" type="date" value={taskForm.end_date} onChange={(e) => setTaskForm({ ...taskForm, end_date: e.target.value })} data-testid="task-end-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Priority" value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} data-testid="task-priority-select">
              {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Select label="Status" value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })} data-testid="task-status-select">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowTaskModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-task-btn">{saving ? 'Saving...' : editingTask ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* ===== Subtask Modal ===== */}
      <Modal isOpen={showSubtaskModal} onClose={() => setShowSubtaskModal(false)} title={editingSubtask ? 'Edit Subtask' : 'New Subtask'}>
        <form onSubmit={handleSubtaskSubmit} className="space-y-4" data-testid="subtask-form">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm" data-testid="subtask-form-error">{error}</div>}
          <Input label="Subtask Name" value={subtaskForm.name} onChange={(e) => setSubtaskForm({ ...subtaskForm, name: e.target.value })} required data-testid="subtask-name-input" />
          <Select label="Assigned To" value={subtaskForm.assigned_to} onChange={(e) => setSubtaskForm({ ...subtaskForm, assigned_to: e.target.value })} data-testid="subtask-assigned-select">
            <option value="">-- Unassigned --</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </Select>
          <Select label="Status" value={subtaskForm.status} onChange={(e) => setSubtaskForm({ ...subtaskForm, status: e.target.value })} data-testid="subtask-status-select">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowSubtaskModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-subtask-btn">{saving ? 'Saving...' : editingSubtask ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
